import React from "react"
import { connect } from "react-redux"
import trans from "trans"

import {
    Grid,
    GridCell,
    Range,
} from "@bdfx/mergado-ui-kit"

import {
    getRealSizeByElementId,
} from "shape-entities/render-text";

import { NumericInput } from "./fields";
import Shape, {CIRCLE_TYPE, DASHED_FRAME_TYPE, FRAME_TYPE, RATING_TYPE, TRIANGLE1_TYPE, TRIANGLE2_TYPE} from "shape-entities/types/shape";
import { getRatingShapeFontSize, getRatingShapeSize } from "template-edit/components/helper";
import RatingShape from "shape-entities/types/Rating";

const { min, max, abs, sin, cos, PI, round, atan2, ceil } = Math

const ShapePositionSizeAndRotationForm = ({ formName, shape, activeShapeId, change, templateSize }) => {
    const subFormName = `shapes.${activeShapeId}`
    const maxWidth = templateSize.width
    const maxHeight = templateSize.height
    const scale = (shape && shape.scale && shape.scale !== 0) ? shape.scale : 1

    const shapePositionSizeAndRotationForm = (shape: Shape): JSX.Element => {
        let minShapeWidth = 0
        let minShapeHeight = 0
        let maxShapeWidth = maxWidth
        let maxShapeHeight = maxHeight

        const { realWidth, realHeight } = getRealSizeByElementId(shape.index, shape.text_fontsize)
        if (realWidth && realHeight) {
            const c = 1.2
            minShapeWidth = round(c * realWidth)
            minShapeHeight = round(c * realHeight)
        }

        switch (shape.type) {
            case FRAME_TYPE:
            case DASHED_FRAME_TYPE:
                return <NumericInput
                    formName={formName}
                    shapeIndex={shape.index}
                    fieldName={'width'}
                    placeholder={'thickness'}
                    id={'width-input'}
                    min={minShapeWidth}
                    max={maxWidth}
                    step={1}
                    units={'px'}
                    value={round(shape.width)}
                />
            case CIRCLE_TYPE:
                return shape.is_aspect_size_ratio ? <NumericInput
                    formName={formName}
                    shapeIndex={shape.index}
                    fieldName={'width'}
                    placeholder={'diameter'}
                    id={'diameter-input'}
                    min={minShapeWidth}
                    max={maxShapeWidth}
                    step={10}
                    units={'px'}
                    value={round(shape.width * scale)}
                    changeValueMethod={(val: number) => round(val / scale)}
                    onChangeFunction={(newValue: number) => {
                        change(formName, `shapes.${shape.index}.height`, newValue)
                    }}
                /> :
                    <Grid cols={'1fr 1fr'}>
                        <GridCell>
                            <NumericInput
                                formName={formName}
                                shapeIndex={shape.index}
                                fieldName={'width'}
                                id={'width-input'}
                                min={minShapeWidth}
                                max={maxShapeWidth}
                                step={10}
                                units={'px'}
                                value={round(shape.width * scale)}
                                changeValueMethod={(val: number) => round(val / scale)}
                            />
                        </GridCell>
                        <GridCell>
                            <NumericInput
                                formName={formName}
                                shapeIndex={shape.index}
                                fieldName={'height'}
                                id={'height-input'}
                                min={minShapeHeight}
                                max={maxShapeHeight}
                                step={10}
                                units={'px'}
                                value={round(shape.height * scale)}
                                changeValueMethod={(val) => round(val / scale)}
                            />
                        </GridCell>
                    </Grid>
            case TRIANGLE1_TYPE:
            case TRIANGLE2_TYPE:
                return <NumericInput
                    formName={formName}
                    shapeIndex={shape.index}
                    fieldName={'width'}
                    placeholder={'triangle_side_length'}
                    id={'width-input'}
                    min={minShapeWidth}
                    max={maxShapeWidth}
                    step={10}
                    units={'px'}
                    value={round(shape.width * scale)}
                    changeValueMethod={(val) => round(val / scale)}
                />
            default:
                return <Grid cols={'1fr 1fr'}>
                    <GridCell>
                        <NumericInput
                            formName={formName}
                            shapeIndex={shape.index}
                            fieldName={'width'}
                            id={'width-input'}
                            min={minShapeWidth}
                            max={maxShapeWidth}
                            step={10}
                            units={'px'}
                            value={round(shape.width * scale)}
                            changeValueMethod={(val) => round(val / scale)}
                            onChangeFunction={(newWidth) => {
                                if (shape.type === RATING_TYPE && newWidth) {
                                    const c = shape.height / shape.width
                                    const newHeight = round(newWidth * c)
                                    const newTextPadding = round(shape.text_padding * c)
                                    const newFontSize = getRatingShapeFontSize(shape as RatingShape, newWidth, newHeight)

                                    change(formName, `${subFormName}`, {
                                        ...shape,
                                        text_fontsize: newFontSize,
                                        text_padding: newTextPadding ? newTextPadding : null,
                                        corner_radius: shape.corner_radius ? round(shape.corner_radius*newHeight/shape.height) : shape.corner_radius,
                                        width: newWidth,
                                        height: newHeight,
                                    })
                                }
                            }}
                        />
                    </GridCell>
                    <GridCell>
                        <NumericInput
                            formName={formName}
                            shapeIndex={shape.index}
                            fieldName={'height'}
                            id={'height-input'}
                            min={minShapeHeight}
                            max={maxShapeHeight}
                            step={10}
                            units={'px'}
                            value={round(shape.height * scale)}
                            changeValueMethod={(val) => round(val / scale)}
                            onChangeFunction={(newHeight) => {
                                if (shape.type === RATING_TYPE && newHeight) {
                                    const c = shape.height / shape.width
                                    const newWidth = round(newHeight / c)
                                    const newTextPadding = round(shape.text_padding * c)
                                    const newFontSize = getRatingShapeFontSize(shape as RatingShape, newWidth, newHeight)

                                    change(formName, `${subFormName}`, {
                                        ...shape,
                                        text_fontsize: newFontSize,
                                        text_padding: newTextPadding ? newTextPadding : null,
                                        corner_radius: shape.corner_radius ? round(shape.corner_radius*newHeight/shape.height) : shape.corner_radius,
                                        width: newWidth,
                                        height: newHeight,
                                    })
                                }
                            }}
                        />
                    </GridCell>
                </Grid>
        }
    }

    return (
        <React.Fragment>
            {![FRAME_TYPE, DASHED_FRAME_TYPE].includes(shape.type) &&
                <React.Fragment>
                    <GridCell>{trans('position')}</GridCell>
                    <GridCell style={{ position: 'relative' }}>
                        <Grid cols={'1fr 1fr'}>
                            <GridCell>
                                <NumericInput
                                    formName={formName}
                                    fieldName={'x'}
                                    shapeIndex={shape.index}
                                    id={'x-pos'}
                                    min={0}
                                    max={maxWidth}
                                    step={10}
                                    value={round(shape.x)}
                                />
                            </GridCell>
                            <GridCell>
                                <NumericInput
                                    formName={formName}
                                    fieldName={'y'}
                                    shapeIndex={shape.index}
                                    id={'y-pos'}
                                    min={0}
                                    max={maxHeight}
                                    step={10}
                                    value={round(shape.y)}
                                />
                            </GridCell>
                        </Grid>
                    </GridCell>
                </React.Fragment>
            }

            <GridCell>{trans('shape_size')}</GridCell>
            <GridCell>{shapePositionSizeAndRotationForm(shape)}</GridCell>

            {![FRAME_TYPE, DASHED_FRAME_TYPE].includes(shape.type) &&
                <React.Fragment>
                    <GridCell style={{alignSelf: 'center'}}>{trans('rotation')}</GridCell>
                    <GridCell>
                        <Range
                            name={`${formName}.${subFormName}.angle`}
                            min={-360}
                            max={360}
                            step={1}
                            defaultValue={0}
                            onChange={(angle) => {
                                change(formName, `${subFormName}.angle`, round(angle > 360 ? 360 : (angle < -360 ? -360 : angle)))
                            }}
                        />
                    </GridCell>
                </React.Fragment>
            }
        </React.Fragment>
    )
}

export default ShapePositionSizeAndRotationForm